import { useState } from 'react';

import { EuBankV2Status } from '@/components/Shared/constants/banks-statuses';
import { Button } from '@/components/Shared/ui';
import ChevronDownIcon from '@/media/icons/chevron-down.svg?react';
import InfoCircle from '@/media/svg/info-circle.svg?react';
import { useAppDispatch, useAppSelector } from '@/store';
import { onSetSelectedBank } from '@/store/financingStep.slice';
import { setCurrent } from '@/store/newOrder.slice';

import useSelectOffer from '../../../hooks/useSelectOffer';
import type { TEurasV2BankOffer } from '../../../types/euras-v2.types';

const hideCancelButtonStatuses = [
  EuBankV2Status.Financed,
  EuBankV2Status.RefundInProgress,
  EuBankV2Status.Refunded
];

interface IProps {
  bankOffer: TEurasV2BankOffer;
  setCancelModal?: (value: boolean) => void;
  setOfferDetailsModal: (value: boolean | string) => void;
}

const useHeaderActions = ({
  bankOffer,
  setCancelModal,
  setOfferDetailsModal
}: IProps) => {
  const dispatch = useAppDispatch();

  const { handleNewSelect } = useSelectOffer({ bankOffer });
  const {
    userAccesses: {
      autoCredit: { canAcceptOffer, canCancelOffer }
    }
  } = useAppSelector((state) => state.userInfo);
  const { orderApp } = useAppSelector((state) => state.newOrder);
  const { selectedBank } = useAppSelector((state) => state.financingStep);

  const [showAlternatives, setShowAlternatives] = useState(false);
  const toggleAlternatives = () => setShowAlternatives(!showAlternatives);

  const canAccept =
    bankOffer.status.code === EuBankV2Status.Approved ||
    bankOffer.status.code === EuBankV2Status.AcceptedAlternative;
  // Блокируем кнопки "Принять" и "Отклонить", если заявка создана клиентом
  // и текущий bankOffer не имеет статуса "Принято клиентом"
  const isButtonsDisabled = canAccept && orderApp?.isOnlineRequest;

  const headerActions: React.ReactElement[] = [];
  if (
    bankOffer.isOffered &&
    bankOffer.status.code !== EuBankV2Status.Financed
  ) {
    headerActions.push(
      <Button
        key={bankOffer.id + 'continue'}
        size="small"
        variant="filled-red"
        onClick={() => {
          if (!selectedBank) dispatch(onSetSelectedBank(bankOffer));
          dispatch(setCurrent(2));
        }}
      >
        Продолжить
      </Button>
    );
  }
  if (canAccept || bankOffer.status.code === EuBankV2Status.ClientAccepted)
    headerActions.push(
      <Button
        key={bankOffer.id + 'accept'}
        size="small"
        variant="filled-red"
        onClick={() => {
          handleNewSelect({
            ...bankOffer,
            offerId: bankOffer.extId || bankOffer.offerId
          });
        }}
        disabled={isButtonsDisabled || !canAcceptOffer}
      >
        Принять <span className="offer-body__hidden">предложение</span>
      </Button>
    );
  if (
    !hideCancelButtonStatuses.includes(
      bankOffer.status.code as EuBankV2Status
    ) &&
    setCancelModal
  ) {
    headerActions.push(
      <Button
        key={bankOffer.id + 'reject'}
        size="small"
        onClick={() => setCancelModal(true)}
        disabled={isButtonsDisabled || !canCancelOffer}
      >
        Отклонить
      </Button>
    );
  }

  if (bankOffer.bankDescription)
    headerActions.push(
      <Button
        key={bankOffer.id + 'description'}
        onClick={() => setOfferDetailsModal(true)}
        size="small"
      >
        <InfoCircle />
        Описание
      </Button>
    );

  if (bankOffer.alternatives && bankOffer.alternatives.length > 0)
    headerActions.push(
      <Button
        className="offer-body__hidden"
        key={bankOffer.id + 'show-alternative'}
        size="small"
        onClick={toggleAlternatives}
      >
        Альтернативных решений: {bankOffer.alternatives?.length}
        <span
          // Стили для этих классов расположены в компоненте OfferCard
          className={`offer-header__chevron ${showAlternatives ? 'offer-header__chevron--active' : ''}`}
        >
          <ChevronDownIcon />
        </span>
      </Button>
    );
  return { headerActions, showAlternatives };
};

export default useHeaderActions;
